import {
  APPROVAL_ROLE_LABELS,
  HANDOVER_STATUS_LABELS,
  hasPermission,
  REQUEST_STATUS_LABELS,
  REQUEST_TYPE_LABELS,
} from "@edm/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, ClipboardList, Plus, RefreshCw, XCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/empty-state";
import { PageHeader } from "@/components/page-header";
import { SignaturePad } from "@/components/signature-pad";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ApiError, apiFetch } from "@/lib/api";
import { useMe } from "@/lib/auth";
import {
  type AccessoryRow,
  type AssetRow,
  createResource,
  type LicenseRow,
  listResource,
  type MaterialRow,
  type RequestRow,
} from "@/lib/master-data";
import { formatDateTime } from "@/lib/utils";

const TYPE_OPTIONS = Object.entries(REQUEST_TYPE_LABELS).map(([value, label]) => ({
  value,
  label,
}));
const CATEGORY_BY_TYPE: Record<string, string> = {
  asset_claim: "asset",
  asset_borrow: "asset",
  accessory_apply: "accessory",
  consumable_issue: "consumable",
  software_seat: "software",
};

const STATUS_VARIANT: Record<string, "default" | "success" | "warning" | "danger" | "secondary"> = {
  pending_approval: "warning",
  approved: "success",
  pending_fulfillment: "warning",
  pending_confirmation: "default",
  completed: "success",
  rejected: "danger",
  cancelled: "secondary",
  confirmation_expired: "danger",
};

function money(cents: number): string {
  return `¥${(cents / 100).toLocaleString("zh-CN", { minimumFractionDigits: 2 })}`;
}

export function RequestsPage({ mode = "requests" }: { mode?: "requests" | "approvals" }) {
  const { data: me } = useMe();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState(mode === "approvals" ? "approvals" : "mine");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;
  const [createOpen, setCreateOpen] = useState(false);
  const [selected, setSelected] = useState<RequestRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [comment, setComment] = useState("");
  const [fulfillAssetId, setFulfillAssetId] = useState("");
  const [fulfillAccessoryId, setFulfillAccessoryId] = useState("");
  const [fulfillLicenseId, setFulfillLicenseId] = useState("");
  const [confirmText, setConfirmText] = useState("本人已核对设备、附件与交接条件，确认接收。");
  const [rejectReason, setRejectReason] = useState("");
  const [signature, setSignature] = useState<string | null>(null);
  const [preflight, setPreflight] = useState<{ canSubmit: boolean; blockers: string[] } | null>(
    null,
  );
  const [preflightLoading, setPreflightLoading] = useState(false);
  const [form, setForm] = useState({
    requestType: "asset_claim",
    materialId: "",
    quantity: "1",
    purpose: "",
    neededBy: "",
    borrowDays: "30",
    months: "12",
  });

  useEffect(() => {
    if (!createOpen || !form.materialId) {
      setPreflight(null);
      return;
    }
    let cancelled = false;
    setPreflightLoading(true);
    apiFetch<{ canSubmit: boolean; blockers: string[] }>("/api/v1/requests/preflight", {
      method: "POST",
      body: JSON.stringify({
        requestType: form.requestType,
        materialId: form.materialId,
        quantity: Number(form.quantity),
        purpose: form.purpose || null,
        neededBy: form.neededBy || null,
        borrowDays: form.requestType === "asset_borrow" ? Number(form.borrowDays) : null,
        months: Number(form.months),
      }),
    })
      .then((result) => {
        if (!cancelled) setPreflight(result);
      })
      .catch(() => {
        if (!cancelled) setPreflight(null);
      })
      .finally(() => {
        if (!cancelled) setPreflightLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    createOpen,
    form.requestType,
    form.materialId,
    form.quantity,
    form.borrowDays,
    form.months,
    form.purpose,
    form.neededBy,
  ]);

  const canManage = Boolean(me && hasPermission(me.permissions, "request:manage"));
  const canApproveProxy = Boolean(me && hasPermission(me.permissions, "request:approve_on_behalf"));
  const scope =
    tab === "mine"
      ? "mine"
      : tab === "approvals"
        ? "approvals"
        : tab === "handovers"
          ? "handovers"
          : "all";
  const listQuery = useQuery({
    queryKey: ["requests", scope, page],
    queryFn: () =>
      apiFetch<{ items: RequestRow[]; total: number }>(
        `/api/v1/requests?scope=${scope}&page=${page}&pageSize=${PAGE_SIZE}`,
      ),
  });
  const materialsQuery = useQuery({
    queryKey: ["materials"],
    queryFn: () => listResource<MaterialRow>("/api/v1/materials"),
  });
  const assetsQuery = useQuery({
    queryKey: ["assets"],
    queryFn: () => listResource<AssetRow>("/api/v1/assets"),
  });
  const accessoriesQuery = useQuery({
    queryKey: ["accessories"],
    queryFn: () => listResource<AccessoryRow>("/api/v1/accessories"),
  });
  const licensesQuery = useQuery({
    queryKey: ["licenses"],
    queryFn: () => listResource<LicenseRow>("/api/v1/licenses"),
  });

  const rows = listQuery.data?.items ?? [];
  const totalRows = listQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalRows / PAGE_SIZE));
  const pageRows = rows;
  const materials = materialsQuery.data ?? [];
  const selectedLive = selected ? (rows.find((row) => row.id === selected.id) ?? selected) : null;
  const pendingHandoverForMe = selectedLive?.handovers.find(
    (handover) => handover.status === "pending" && handover.recipientId === me?.user.id,
  );
  const pendingStepForMe = selectedLive?.steps.find(
    (step) => step.status === "pending" && (step.approverUserId === me?.user.id || canApproveProxy),
  );
  const matchingCategory = selectedLive ? CATEGORY_BY_TYPE[selectedLive.requestType] : "";
  const availableAssets = (assetsQuery.data ?? []).filter(
    (asset) => asset.status === "in_stock" && asset.materialId === selectedLive?.materialId,
  );
  const availableAccessories = (accessoriesQuery.data ?? []).filter(
    (row) => row.status === "in_stock" && row.materialId === selectedLive?.materialId,
  );
  const availableLicenses = (licensesQuery.data ?? []).filter(
    (row) => row.materialId === selectedLive?.materialId && row.allocatedSeats < row.totalSeats,
  );
  const requestMaterials = materials.filter(
    (material) =>
      material.category === CATEGORY_BY_TYPE[form.requestType] && material.status === "active",
  );

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ["requests"] });
    queryClient.invalidateQueries({ queryKey: ["assets"] });
    queryClient.invalidateQueries({ queryKey: ["accessories"] });
    queryClient.invalidateQueries({ queryKey: ["licenses"] });
    queryClient.invalidateQueries({ queryKey: ["consumable-stock"] });
  }

  async function createRequest() {
    if (!form.materialId) return toast.error("请选择物料");
    setBusy(true);
    try {
      await createResource("/api/v1/requests", {
        requestType: form.requestType,
        materialId: form.materialId,
        quantity: Number(form.quantity),
        purpose: form.purpose || null,
        neededBy: form.neededBy || null,
        borrowDays: form.requestType === "asset_borrow" ? Number(form.borrowDays) : null,
        months: Number(form.months),
      });
      toast.success("申请已提交");
      setCreateOpen(false);
      setForm({ ...form, materialId: "", purpose: "" });
      invalidate();
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "提交失败，请补充组织关系");
    } finally {
      setBusy(false);
    }
  }

  async function decide(decision: "approve" | "reject") {
    if (!selectedLive) return;
    setBusy(true);
    try {
      await createResource(`/api/v1/requests/${selectedLive.id}/decide`, {
        decision,
        comment: comment || null,
      });
      toast.success(decision === "approve" ? "已批准" : "已退回");
      setSelected(null);
      setComment("");
      invalidate();
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "审批失败");
    } finally {
      setBusy(false);
    }
  }

  async function fulfill() {
    if (!selectedLive) return;
    setBusy(true);
    try {
      await createResource("/api/v1/handovers", {
        requestId: selectedLive.id,
        assetId: fulfillAssetId || null,
        accessoryId: fulfillAccessoryId || null,
        licenseId: fulfillLicenseId || null,
      });
      toast.success("交接已发起，等待接收人确认");
      setSelected(null);
      invalidate();
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "办理失败");
    } finally {
      setBusy(false);
    }
  }

  async function decideHandover(decision: "confirm" | "reject") {
    if (!pendingHandoverForMe) return;
    setBusy(true);
    try {
      await createResource(`/api/v1/handovers/${pendingHandoverForMe.id}/decide`, {
        decision,
        confirmationText: decision === "confirm" ? confirmText : null,
        rejectedReason: decision === "reject" ? rejectReason : null,
        signatureData: decision === "confirm" ? signature : null,
      });
      toast.success(decision === "confirm" ? "已确认接收" : "已拒绝交接");
      setSelected(null);
      setRejectReason("");
      invalidate();
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "确认失败");
    } finally {
      setBusy(false);
    }
  }

  async function cancelRequest() {
    if (!selectedLive) return;
    setBusy(true);
    try {
      await createResource(`/api/v1/requests/${selectedLive.id}/cancel`, {});
      toast.success("申请已取消");
      setSelected(null);
      invalidate();
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "取消失败");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={mode === "approvals" ? "待办审批" : "业务办理"}
        description={
          mode === "approvals"
            ? "按审批矩阵处理待办；成本中心负责人、直线上级与 CEO 终局均在此完成，系统管理员可代批并留痕。"
            : "发起资产/低值易耗/耗材/软件席位申请；审批通过后由设备管理员发起交接，接收人确认后完成责任转移。"
        }
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => listQuery.refetch()}
              disabled={listQuery.isFetching}
            >
              <RefreshCw className="h-4 w-4" />
              刷新
            </Button>
            {mode === "requests" ? (
              <Button onClick={() => setCreateOpen(true)}>
                <Plus className="h-4 w-4" />
                新建申请
              </Button>
            ) : null}
          </>
        }
      />

      {mode === "requests" ? (
        <Tabs
          value={tab}
          onValueChange={(value) => {
            setTab(value);
            setPage(1);
          }}
        >
          <TabsList className="flex-wrap">
            <TabsTrigger value="mine">我的申请</TabsTrigger>
            <TabsTrigger value="handovers">待我确认</TabsTrigger>
            {canManage ? <TabsTrigger value="all">全部申请</TabsTrigger> : null}
          </TabsList>
        </Tabs>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>
            {mode === "approvals" ? "待我审批" : tab === "handovers" ? "待我确认交接" : "申请列表"}
          </CardTitle>
          <CardDescription>
            确认超时为 3 个工作日；超时后申请回到待办理，不会自动签收。
          </CardDescription>
        </CardHeader>
        <CardContent>
          {listQuery.isLoading ? (
            <div className="space-y-2">
              {["r1", "r2", "r3", "r4"].map((key) => (
                <Skeleton key={key} className="h-11 w-full" />
              ))}
            </div>
          ) : totalRows === 0 ? (
            <EmptyState
              title={
                mode === "approvals"
                  ? "暂无待办审批"
                  : tab === "handovers"
                    ? "暂无待确认交接"
                    : "暂无申请"
              }
              description={
                mode === "approvals" ? "当前没有分配给你的审批步骤。" : "发起申请后会出现在这里。"
              }
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>申请编号</TableHead>
                  <TableHead>类型</TableHead>
                  <TableHead>物料 / 数量</TableHead>
                  <TableHead>金额</TableHead>
                  <TableHead>申请人</TableHead>
                  <TableHead>状态</TableHead>
                  <TableHead>提交时间</TableHead>
                  <TableHead className="text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageRows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-mono text-xs">{row.requestNo}</TableCell>
                    <TableCell>
                      {REQUEST_TYPE_LABELS[row.requestType as keyof typeof REQUEST_TYPE_LABELS] ??
                        row.requestType}
                    </TableCell>
                    <TableCell>
                      {row.material?.name ?? "—"} × {row.quantity}
                    </TableCell>
                    <TableCell>{money(row.amountCents)}</TableCell>
                    <TableCell>{row.requester?.displayName ?? "—"}</TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANT[row.status] ?? "secondary"}>
                        {REQUEST_STATUS_LABELS[row.status as keyof typeof REQUEST_STATUS_LABELS] ??
                          row.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatDateTime(row.createdAt)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => setSelected(row)}>
                        查看 / 办理
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {!listQuery.isLoading && totalRows > 0 ? (
            <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
              <span>
                共 {totalRows} 条 · 第 {page} / {totalPages} 页
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((value) => value - 1)}
                >
                  上一页
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((value) => value + 1)}
                >
                  下一页
                </Button>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>新建业务申请</DialogTitle>
            <DialogDescription>
              提交后按 SOP 4.4 自动选择审批人；耗材与低于 100 元的软件无需审批。
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>申请类型</Label>
              <select
                value={form.requestType}
                onChange={(event) =>
                  setForm({ ...form, requestType: event.target.value, materialId: "" })
                }
                className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm"
              >
                {TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>数量</Label>
              <Input
                type="number"
                min="1"
                value={form.quantity}
                onChange={(event) => setForm({ ...form, quantity: event.target.value })}
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>物料</Label>
              <select
                value={form.materialId}
                onChange={(event) => setForm({ ...form, materialId: event.target.value })}
                className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm"
              >
                <option value="">请选择物料</option>
                {requestMaterials.map((material) => (
                  <option key={material.id} value={material.id}>
                    {material.code} · {material.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>期望使用日期</Label>
              <Input
                type="date"
                value={form.neededBy}
                onChange={(event) => setForm({ ...form, neededBy: event.target.value })}
              />
            </div>
            {form.requestType === "asset_borrow" ? (
              <div className="space-y-2">
                <Label>借用天数</Label>
                <Input
                  type="number"
                  min="1"
                  value={form.borrowDays}
                  onChange={(event) => setForm({ ...form, borrowDays: event.target.value })}
                />
              </div>
            ) : null}
            {form.requestType === "software_seat" ? (
              <div className="space-y-2">
                <Label>申请时长（月）</Label>
                <Input
                  type="number"
                  min="1"
                  max="36"
                  value={form.months}
                  onChange={(event) => setForm({ ...form, months: event.target.value })}
                />
              </div>
            ) : null}
            <div className="space-y-2 sm:col-span-2">
              <Label>用途说明</Label>
              <Input
                value={form.purpose}
                onChange={(event) => setForm({ ...form, purpose: event.target.value })}
                placeholder="例如：新员工入职设备"
              />
            </div>
          </div>
          {preflightLoading ? (
            <p className="text-xs text-muted-foreground">正在预检审批人与库存…</p>
          ) : preflight && !preflight.canSubmit ? (
            <Alert variant="warning" title="暂不能提交">
              <ul className="list-disc pl-4">
                {preflight.blockers.map((blocker) => (
                  <li key={blocker}>{blocker}</li>
                ))}
              </ul>
            </Alert>
          ) : preflight?.canSubmit ? (
            <p className="text-xs text-emerald-700">预检通过，将按当前审批矩阵生成审批链。</p>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              取消
            </Button>
            <Button
              onClick={createRequest}
              disabled={busy || preflightLoading || preflight?.canSubmit === false}
            >
              提交申请
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(selected)} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{selectedLive?.requestNo}</DialogTitle>
            <DialogDescription>
              {selectedLive
                ? REQUEST_TYPE_LABELS[selectedLive.requestType as keyof typeof REQUEST_TYPE_LABELS]
                : ""}{" "}
              · {selectedLive?.material?.name ?? ""} × {selectedLive?.quantity ?? 0} ·{" "}
              {money(selectedLive?.amountCents ?? 0)}
            </DialogDescription>
          </DialogHeader>

          {selectedLive ? (
            <div className="max-h-[60vh] space-y-4 overflow-auto pr-1">
              <div className="grid gap-2 rounded-lg bg-muted/60 p-3 text-sm sm:grid-cols-2">
                <p>申请人：{selectedLive.requester?.displayName ?? "—"}</p>
                <p>
                  状态：
                  <Badge variant={STATUS_VARIANT[selectedLive.status] ?? "secondary"}>
                    {REQUEST_STATUS_LABELS[
                      selectedLive.status as keyof typeof REQUEST_STATUS_LABELS
                    ] ?? selectedLive.status}
                  </Badge>
                </p>
                <p>用途：{selectedLive.purpose ?? "—"}</p>
                <p>期望日期：{selectedLive.neededBy ?? "—"}</p>
              </div>

              <div>
                <p className="mb-2 text-sm font-medium">审批进度</p>
                <div className="space-y-2">
                  {selectedLive.steps.length === 0 ? (
                    <p className="text-xs text-muted-foreground">无需审批</p>
                  ) : null}
                  {selectedLive.steps.map((step) => (
                    <div
                      key={step.id}
                      className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-xs"
                    >
                      <span>
                        第 {step.stepNo} 步 ·{" "}
                        {APPROVAL_ROLE_LABELS[
                          step.approverRole as keyof typeof APPROVAL_ROLE_LABELS
                        ] ?? step.approverRole}
                      </span>
                      <Badge
                        variant={
                          step.status === "approved"
                            ? "success"
                            : step.status === "rejected"
                              ? "danger"
                              : "secondary"
                        }
                      >
                        {step.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>

              {selectedLive.handovers.length > 0 ? (
                <div>
                  <p className="mb-2 text-sm font-medium">交接记录</p>
                  {selectedLive.handovers.map((handover) => (
                    <div
                      key={handover.id}
                      className="mb-2 rounded-md border border-border p-3 text-xs"
                    >
                      <p>
                        状态：
                        {HANDOVER_STATUS_LABELS[
                          handover.status as keyof typeof HANDOVER_STATUS_LABELS
                        ] ?? handover.status}{" "}
                        · 截止 {formatDateTime(handover.dueAt)}
                      </p>
                      {handover.confirmationText ? (
                        <p className="mt-1 text-muted-foreground">
                          确认声明：{handover.confirmationText}
                        </p>
                      ) : null}
                      {handover.rejectedReason ? (
                        <p className="mt-1 text-red-700">拒绝原因：{handover.rejectedReason}</p>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : null}

              {pendingStepForMe && selectedLive.status === "pending_approval" ? (
                <div className="space-y-2 rounded-lg border border-border p-3">
                  <p className="text-sm font-medium">审批操作</p>
                  {pendingStepForMe.approverUserId !== me?.user.id ? (
                    <p className="text-xs text-amber-700">系统管理员代批，审计会记录被代批人。</p>
                  ) : null}
                  <Input
                    value={comment}
                    onChange={(event) => setComment(event.target.value)}
                    placeholder="审批意见（拒绝时必填）"
                  />
                  <div className="flex justify-end gap-2">
                    <Button variant="danger" onClick={() => decide("reject")} disabled={busy}>
                      <XCircle className="h-4 w-4" />
                      退回
                    </Button>
                    <Button onClick={() => decide("approve")} disabled={busy}>
                      <CheckCircle2 className="h-4 w-4" />
                      批准
                    </Button>
                  </div>
                </div>
              ) : null}

              {pendingHandoverForMe ? (
                <div className="space-y-2 rounded-lg border border-border p-3">
                  <p className="text-sm font-medium">接收确认</p>
                  <Input
                    value={confirmText}
                    onChange={(event) => setConfirmText(event.target.value)}
                  />
                  <Input
                    value={rejectReason}
                    onChange={(event) => setRejectReason(event.target.value)}
                    placeholder="拒绝时填写原因"
                  />
                  <SignaturePad onChange={setSignature} />
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => decideHandover("reject")}
                      disabled={busy}
                    >
                      拒绝接收
                    </Button>
                    <Button onClick={() => decideHandover("confirm")} disabled={busy}>
                      确认接收
                    </Button>
                  </div>
                </div>
              ) : null}

              {canManage &&
              ["approved", "pending_fulfillment", "confirmation_expired"].includes(
                selectedLive.status,
              ) ? (
                <div className="space-y-3 rounded-lg border border-border p-3">
                  <p className="text-sm font-medium">发起交接</p>
                  {matchingCategory === "asset" ? (
                    <select
                      value={fulfillAssetId}
                      onChange={(event) => setFulfillAssetId(event.target.value)}
                      className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm"
                    >
                      <option value="">选择在库资产</option>
                      {availableAssets.map((asset) => (
                        <option key={asset.id} value={asset.id}>
                          {asset.assetCode} · {asset.serialNumber ?? ""}
                        </option>
                      ))}
                    </select>
                  ) : matchingCategory === "accessory" ? (
                    <select
                      value={fulfillAccessoryId}
                      onChange={(event) => setFulfillAccessoryId(event.target.value)}
                      className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm"
                    >
                      <option value="">选择在库低值易耗</option>
                      {availableAccessories.map((row) => (
                        <option key={row.id} value={row.id}>
                          {row.code}
                        </option>
                      ))}
                    </select>
                  ) : matchingCategory === "software" ? (
                    <select
                      value={fulfillLicenseId}
                      onChange={(event) => setFulfillLicenseId(event.target.value)}
                      className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm"
                    >
                      <option value="">选择有可用席位的许可</option>
                      {availableLicenses.map((license) => (
                        <option key={license.id} value={license.id}>
                          {license.licenseCode} · 余 {license.totalSeats - license.allocatedSeats}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      耗材无需交接，直接发放并扣减库存。
                    </p>
                  )}
                  <Button onClick={fulfill} disabled={busy}>
                    {matchingCategory === "consumable" ? "直接发放" : "发起交接"}
                  </Button>
                </div>
              ) : null}

              {selectedLive.requesterId === me?.user.id &&
              [
                "pending_approval",
                "approved",
                "pending_fulfillment",
                "confirmation_expired",
              ].includes(selectedLive.status) ? (
                <Button variant="outline" onClick={cancelRequest} disabled={busy}>
                  <ClipboardList className="h-4 w-4" />
                  取消申请
                </Button>
              ) : null}
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)}>
              关闭
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function ApprovalsPage() {
  return <RequestsPage mode="approvals" />;
}
